// src/pages/History.js

import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Typography, 
    Button, 
    Paper, 
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    Menu,
    Avatar,
    InputAdornment,
    Tooltip,
    Alert,
    Snackbar,
    Tabs,
    Tab,
    Card,
    CardContent,
    CircularProgress,
    FormHelperText,
    Badge,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import { 
    Delete, 
    Search,
    Receipt,
    Close as CloseIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Today as TodayIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    PictureAsPdf,
    TableChart, 
    GetApp,
    Print,
    Person,
    Phone,
    CalendarMonth,
    TrendingUp,
    TrendingDown,
    AccountBalance,
    Description,
    Money,
    ShoppingCart,
    SwapHoriz,
    CheckCircle,
    Cancel,
    Pending,
    Assignment,
    MoreVert,
    Scale,
    DateRange,
    Weekend,
    EventNote,
    CalendarToday,
    Timeline
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIOrdersList,
    APICategoriesList,
    APIOrdersExportExcel,
    APIOrdersExportPDF,
    APIOrdersPrint,
    APIOrdersStats,
    APIOrdersDelete,
    APIOrdersUpdateStatus,
    APIUnitName
} from '../../apiServices/APIs';
import { formatCreatedAtDate, formatNumberWithComma, recordsCount, recordsPerPage } from '../../utils/helpers';
import { getUserDetails } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import { usePermission } from '../../hooks/usePermission';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorrState';
import SystemInformation from '../../components/SystemInformation';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const History = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('DELETE');
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
    const [selectedOrderForAction, setSelectedOrderForAction] = useState(null);
    const [pageState, setPageState] = useState({
        ordersData: [],
        categoriesData: [],
        unitsData: []
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    const [statistics, setStatistics] = useState(null);
    const [dateRangeLabel, setDateRangeLabel] = useState('');
    
    // Status update states
    const [openStatusDialog, setOpenStatusDialog] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [statusError, setStatusError] = useState('');
    
    const [filters, setFilters] = useState({
        customerName: '',
        customerPhone: '',
        orderType: '',
        paymentMethod: '',
        orderStatus: '',
        dateFrom: '',
        dateTo: ''
    });

    // Quick filter states
    const [quickFilter, setQuickFilter] = useState('all');

    const [filterOptions, setFilterOptions] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const { hasPermission, loading: permissionLoading } = usePermission();

    useEffect(() => {
        apiService.setToastFunction(showToast);
        fetchData();
        fetchStatistics();
    }, [showToast]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const userBranchId = getUserDetails("branchId");
            const [ordersResult, categoriesResult, unitsResult] = await Promise.all([
                apiService.list(userBranchId, APIOrdersList),
                apiService.list(userBranchId, APICategoriesList),
                apiService.list(userBranchId, APIUnitName)
            ]);
            if (ordersResult.success && categoriesResult.success && unitsResult.success) {
                const orders = Array.isArray(ordersResult.data) ? ordersResult.data : [];
                const categories = Array.isArray(categoriesResult.data) ? categoriesResult.data : [];
                const units = Array.isArray(unitsResult.data) ? unitsResult.data : [];
                setPageState(prev => ({ 
                    ...prev, 
                    ordersData: orders, 
                    categoriesData: categories,
                    unitsData: units
                }));
            } else {
                setError(ordersResult.message || categoriesResult.message || unitsResult.message);
            }
        } catch (error) {
            setError("Failed to load data");
        } finally {
            setLoading(false);
        }
    };

    const fetchStatistics = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.listStats(userBranchId, APIOrdersStats);
            if (result.success && result.data) setStatistics(result.data);
        } catch (error) {
            console.error("Error fetching statistics:", error);
        }
    };

    // Helper function to get unit name
    const getUnitName = (unitId) => {
        if (!unitId) return 'N/A';
        const unit = pageState.unitsData.find(u => u.id === unitId);
        return unit ? `${unit.name} (${unit.code})` : 'N/A';
    };

    // Date range helper functions
    const getDateRange = (range) => {
        const today = new Date();
        const start = new Date(today);
        const end = new Date(today);
        
        switch(range) {
            case 'today':
                // Today: start and end are today
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                setDateRangeLabel('Today');
                break;
            case 'yesterday':
                // Yesterday
                start.setDate(today.getDate() - 1);
                start.setHours(0, 0, 0, 0);
                end.setDate(today.getDate() - 1);
                end.setHours(23, 59, 59, 999);
                setDateRangeLabel('Yesterday');
                break;
            case 'this_week':
                // This week (Monday to Sunday)
                const dayOfWeek = today.getDay();
                const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
                start.setDate(diff);
                start.setHours(0, 0, 0, 0);
                end.setDate(diff + 6);
                end.setHours(23, 59, 59, 999);
                setDateRangeLabel('This Week');
                break;
            case 'this_month':
                // This month
                start.setDate(1);
                start.setHours(0, 0, 0, 0);
                end.setMonth(today.getMonth() + 1);
                end.setDate(0);
                end.setHours(23, 59, 59, 999);
                setDateRangeLabel('This Month');
                break;
            case 'last_month':
                // Last month
                start.setMonth(today.getMonth() - 1);
                start.setDate(1);
                start.setHours(0, 0, 0, 0);
                end.setMonth(today.getMonth());
                end.setDate(0);
                end.setHours(23, 59, 59, 999);
                setDateRangeLabel('Last Month');
                break;
            case 'this_quarter':
                // This quarter
                const quarter = Math.floor(today.getMonth() / 3);
                start.setMonth(quarter * 3);
                start.setDate(1);
                start.setHours(0, 0, 0, 0);
                end.setMonth(quarter * 3 + 3);
                end.setDate(0);
                end.setHours(23, 59, 59, 999);
                setDateRangeLabel('This Quarter');
                break;
            case 'last_quarter':
                // Last quarter
                const lastQuarter = Math.floor((today.getMonth() - 3) / 3);
                const lastQuarterMonth = lastQuarter * 3;
                start.setMonth(lastQuarterMonth);
                start.setDate(1);
                start.setHours(0, 0, 0, 0);
                end.setMonth(lastQuarterMonth + 3);
                end.setDate(0);
                end.setHours(23, 59, 59, 999);
                setDateRangeLabel('Last Quarter');
                break;
            case 'this_year':
                // This year
                start.setMonth(0);
                start.setDate(1);
                start.setHours(0, 0, 0, 0);
                end.setMonth(11);
                end.setDate(31);
                end.setHours(23, 59, 59, 999);
                setDateRangeLabel('This Year');
                break;
            case 'last_7_days':
                // Last 7 days
                start.setDate(today.getDate() - 7);
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                setDateRangeLabel('Last 7 Days');
                break;
            case 'last_30_days':
                // Last 30 days
                start.setDate(today.getDate() - 30);
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                setDateRangeLabel('Last 30 Days');
                break;
            case 'all':
            default:
                // All time - don't set date filters
                start.setFullYear(2000, 0, 1);
                start.setHours(0, 0, 0, 0);
                end.setFullYear(2100, 11, 31);
                end.setHours(23, 59, 59, 999);
                setDateRangeLabel('All Time');
                break;
        }
        
        return { start, end };
    };

    // Apply quick filter
    const applyQuickFilter = (range) => {
        setQuickFilter(range);
        
        if (range === 'all') {
            setFilters(prev => ({
                ...prev,
                dateFrom: '',
                dateTo: ''
            }));
            setDateRangeLabel('All Time');
            handleCloseOptions();
            return;
        }
        
        const { start, end } = getDateRange(range);
        setFilters(prev => ({
            ...prev,
            dateFrom: start.toISOString().split('T')[0],
            dateTo: end.toISOString().split('T')[0]
        }));
        handleCloseOptions();
    };

    // Status update handlers
    const handleOpenStatusDialog = (order) => {
        setSelectedRecord(order);
        setSelectedStatus(order.order_status);
        setStatusError('');
        setOpenStatusDialog(true);
        handleActionMenuClose();
    };

    const handleCloseStatusDialog = () => {
        setOpenStatusDialog(false);
        setSelectedRecord(null);
        setSelectedStatus('');
        setStatusError('');
    };

    const handleStatusChange = (e) => {
        setSelectedStatus(e.target.value);
        setStatusError('');
    };

    const handleUpdateStatus = async () => {
        if (!selectedStatus) {
            setStatusError('Please select a status');
            return;
        }

        if (selectedStatus === selectedRecord?.order_status) {
            setStatusError('Status is already set to ' + getStatusLabel(selectedStatus));
            return;
        }

        setUpdatingStatus(true);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('user_id', getUserDetails("Id"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('updateId', selectedRecord.id);
            formDataToSend.append('order_status', selectedStatus);

            const result = await apiService.update(APIOrdersUpdateStatus, formDataToSend);

            if (result && result.success) {
                showToast("Success", result.message || "Status updated successfully", "success");
                handleCloseStatusDialog();
                fetchData();
                fetchStatistics();
            }
        } catch (error) {
            console.error("Status update error:", error);
            showToast("Error", error.message || "Failed to update status", "error");
        } finally {
            setUpdatingStatus(false);
        }
    };

    // Export handlers
    const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
    const handleExportClose = () => { setExportAnchorEl(null); };

    const handlePrint = async (record) => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to print order details", "error");
            return;
        }
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('print_id', record.id);
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.print(APIOrdersPrint, formDataToSend);
            if (result && result.success) {
                showToast("Success", "PDF file generated successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to generate PDF file", "error");
            }
        } catch (error) {
            showToast("Error", "Failed to generate PDF file", "error");
        }
    };

    const handleExportExcel = async () => {
        if (!hasPermission('EXPORT')) { handleExportClose(); return; }
        try {
            handleExportClose();
            showToast("Processing", "Preparing Excel export...", "info");
            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            if (filters.dateFrom) formDataToSend.append('date_from', filters.dateFrom);
            if (filters.dateTo) formDataToSend.append('date_to', filters.dateTo);
            if (filters.orderType) formDataToSend.append('order_type', filters.orderType);
            if (filters.orderStatus) formDataToSend.append('order_status', filters.orderStatus);
            const result = await apiService.exportToExcel(APIOrdersExportExcel, formDataToSend);
            if (result && result.success) showToast("Success", "Excel file downloaded successfully", "success");
        } catch (error) {
            showToast("Error", error.message || "Failed to export Excel file", "error");
        }
    };

    const handleExportPDF = async () => {
        if (!hasPermission('EXPORT')) { handleExportClose(); return; }
        try {
            handleExportClose();
            showToast("Processing", "Preparing PDF export...", "info");
            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            if (filters.dateFrom) formDataToSend.append('date_from', filters.dateFrom);
            if (filters.dateTo) formDataToSend.append('date_to', filters.dateTo);
            const result = await apiService.exportToPDF(APIOrdersExportPDF, formDataToSend);
            if (result && result.success) showToast("Success", "PDF file downloaded successfully", "success");
        } catch (error) {
            showToast("Error", error.message || "Failed to export PDF file", "error");
        }
    };

    const filteredData = pageState.ordersData.filter(order => {
        const matchesSearch = searchTerm === '' ||
            order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.customer_phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.id?.toString().includes(searchTerm);
        const matchesOrderType = !filters.orderType || order.order_type === filters.orderType;
        const matchesOrderStatus = !filters.orderStatus || order.order_status === filters.orderStatus;
        let matchesDate = true;
        if (filters.dateFrom) {
            const orderDate = new Date(order.created_at).toISOString().split('T')[0];
            matchesDate = matchesDate && orderDate >= filters.dateFrom;
        }
        if (filters.dateTo) {
            const orderDate = new Date(order.created_at).toISOString().split('T')[0];
            matchesDate = matchesDate && orderDate <= filters.dateTo;
        }
        return matchesSearch && matchesOrderType && matchesOrderStatus && matchesDate;
    });

    // Calculate filtered totals
    const calculateFilteredTotals = () => {
        const totalAmount = filteredData.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0);
        const totalBalance = filteredData.reduce((sum, order) => sum + parseFloat(order.balance || 0), 0);
        const totalCashGiven = filteredData.reduce((sum, order) => sum + parseFloat(order.customer_given_cash || 0), 0);
        
        const directPayments = filteredData.filter(order => order.order_type === 'direct_payment').length;
        const loanOrders = filteredData.filter(order => order.order_type === 'loan_order').length;
        const completedOrders = filteredData.filter(order => order.order_status === 'completed').length;
        const pendingOrders = filteredData.filter(order => order.order_status === 'pending').length;
        const quotationOrders = filteredData.filter(order => order.order_status === 'quotation').length;
        const cancelledOrders = filteredData.filter(order => order.order_status === 'cancelled').length;

        return {
            totalAmount,
            totalBalance,
            totalCashGiven,
            directPayments,
            loanOrders,
            completedOrders,
            pendingOrders,
            quotationOrders,
            cancelledOrders,
            totalCount: filteredData.length
        };
    };

    const filteredTotals = calculateFilteredTotals();

    const calculateTotals = () => {
        if (statistics) {
            return {
                total: statistics.total_orders || 0,
                totalAmount: statistics.total_revenue || 0,
                completed: statistics.completed_orders || 0,
                pending: statistics.pending_orders || 0
            };
        }
        return { total: 0, totalAmount: 0, completed: 0, pending: 0 };
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-UG', {
            style: 'currency', currency: 'UGX', minimumFractionDigits: 0, maximumFractionDigits: 0
        }).format(amount || 0);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleFilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);

    const handleOpenView = (order) => {
        setSelectedRecord(order);
        setActiveTab(0);
        setOpenView(true);
        handleActionMenuClose();
    };
    const handleCloseView = () => { setOpenView(false); setActiveTab(0); };

    const handleOpenDelete = (order) => {
        if (!hasPermission('DELETE')) { showToast("Permission Denied", "You don't have permission to delete orders", "error"); return; }
        setSelectedRecord(order);
        setUpdateId(order.id);
        setOpenDelete(true);
        handleActionMenuClose();
    };
    const handleCloseDelete = () => setOpenDelete(false);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const clearFilters = () => {
        setFilters({ customerName: '', customerPhone: '', orderType: '', paymentMethod: '', orderStatus: '', dateFrom: '', dateTo: '' });
        setSearchTerm('');
        setQuickFilter('all');
        setDateRangeLabel('');
        handleCloseOptions();
    };

    const applyFilters = () => handleCloseOptions();

    // Action menu handlers
    const handleActionMenuOpen = (event, order) => {
        event.stopPropagation();
        setActionMenuAnchorEl(event.currentTarget);
        setSelectedOrderForAction(order);
    };

    const handleActionMenuClose = () => {
        setActionMenuAnchorEl(null);
        setSelectedOrderForAction(null);
    };

    const handleDeleteOrder = async () => {
        if (!hasPermission('DELETE')) return;
        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));
        formDataToSend.append('updateId', updateId);
        try {
            const result = await apiService.delete(APIOrdersDelete, formDataToSend);
            if (result && result.success) {
                showToast("Success", "Order deleted successfully", "success");
                handleCloseDelete();
                fetchData();
                fetchStatistics();
            }
        } catch (error) {
            console.error("API Error:", error);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'success';
            case 'pending': return 'warning';
            case 'quotation': return 'info';
            case 'cancelled': return 'error';
            default: return 'default';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed': return <CheckCircle fontSize="small" />;
            case 'pending': return <Pending fontSize="small" />;
            case 'quotation': return <Assignment fontSize="small" />;
            case 'cancelled': return <Cancel fontSize="small" />;
            default: return null;
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'completed': return 'Completed';
            case 'pending': return 'Pending';
            case 'quotation': return 'Quotation';
            case 'cancelled': return 'Cancelled';
            default: return status;
        }
    };

    const getOrderTypeColor = (type) => {
        switch (type) {
            case 'direct_payment': return 'success';
            case 'loan_order': return 'warning';
            default: return 'default';
        }
    };

    const totals = calculateTotals();

    if (permissionLoading) return <LoadingState message="Loading permissions..." variant="medical" size="large" fullPage />;
    if (loading) return <LoadingState message="Loading Order History..." variant="medical" size="large" fullPage />;
    if (error) return <ErrorState error={error} onRetry={fetchData} title="Unable to Load Order History" variant="medical" fullPage retryText="Retry Loading" />;

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Total Orders</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>{totals.total}</Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.primary.light, width: 48, height: 48 }}><Receipt /></Avatar>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Total Revenue</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>{formatCurrency(totals.totalAmount)}</Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.success.light, width: 48, height: 48 }}><Money /></Avatar>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Completed</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.info.main }}>{totals.completed}</Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.info.light, width: 48, height: 48 }}><TrendingUp /></Avatar>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Pending</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>{totals.pending}</Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.warning.light, width: 48, height: 48 }}><TrendingDown /></Avatar>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Search and Filter Bar */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, flexShrink: 0 }}>
                        Order History ({recordsCount(pageState.ordersData.length)} Total)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {hasPermission('VIEW') && (
                            <Button variant="contained" color="secondary" startIcon={<FilterIcon />} onClick={handleFilterOptions} sx={{ textTransform: 'none', borderRadius: '8px' }} size="small">Filters</Button>
                        )}
                        {hasPermission('EXPORT') && (
                            <Button variant="contained" color="primary" startIcon={<GetApp />} onClick={handleExportClick} sx={{ textTransform: 'none', borderRadius: '8px' }} size="small">Export</Button>
                        )}
                        <Button variant="outlined" color="info" startIcon={<RefreshIcon />} onClick={() => { fetchData(); fetchStatistics(); }} sx={{ textTransform: 'none', borderRadius: '8px' }} size="small">Refresh</Button>
                    </Box>
                    <Paper sx={{ width: '300px', p: '2px 8px', display: 'flex', alignItems: 'center', marginLeft: 'auto', borderRadius: '8px', background: theme.palette.primary.light }}>
                        <TextField fullWidth variant="outlined" placeholder="Search orders..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: { '& fieldset': { border: 'none' }, fontSize: '0.875rem', color: 'white', '& input': { color: 'white', '&::placeholder': { color: 'rgba(255, 255, 255, 0.7)', opacity: 1 } } }
                            }} size="small" />
                    </Paper>
                </Box>
                {/* Date Range Indicator */}
                {dateRangeLabel && (
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                            icon={<DateRange />} 
                            label={`Showing: ${dateRangeLabel}`} 
                            color="primary" 
                            size="small"
                            onDelete={() => {
                                setDateRangeLabel('');
                                setFilters(prev => ({ ...prev, dateFrom: '', dateTo: '' }));
                                setQuickFilter('all');
                            }}
                        />
                        {filters.dateFrom && filters.dateTo && dateRangeLabel === 'Custom' && (
                            <Typography variant="caption" color="text.secondary">
                                {new Date(filters.dateFrom).toLocaleDateString()} - {new Date(filters.dateTo).toLocaleDateString()}
                            </Typography>
                        )}
                    </Box>
                )}
            </Paper>

            {/* Export Menu */}
            <Menu anchorEl={exportAnchorEl} open={Boolean(exportAnchorEl)} onClose={handleExportClose}>
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}><TableChart fontSize="small" color="success" /> Export to Excel (.xlsx)</MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}><PictureAsPdf fontSize="small" color="error" /> Export to PDF (.pdf)</MenuItem>
            </Menu>

            {/* Action Menu for List Items */}
            <Menu
                anchorEl={actionMenuAnchorEl}
                open={Boolean(actionMenuAnchorEl)}
                onClose={handleActionMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {hasPermission('VIEW') && (
                    <MenuItem onClick={() => selectedOrderForAction && handleOpenView(selectedOrderForAction)}>
                        <ListItemIcon><ViewIcon fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText sx={{ color: 'primary.main' }}>View Details</ListItemText>
                    </MenuItem>
                )}
                {selectedOrderForAction && selectedOrderForAction.order_status !== 'cancelled' && selectedOrderForAction.order_status !== 'completed' && (
                    <MenuItem onClick={() => selectedOrderForAction && handleOpenStatusDialog(selectedOrderForAction)}>
                        <ListItemIcon><SwapHoriz fontSize="small" color="secondary" /></ListItemIcon>
                        <ListItemText sx={{ color: 'secondary.main' }}>Change Status</ListItemText>
                    </MenuItem>
                )}
                {hasPermission('PRINT') && (
                    <MenuItem onClick={() => selectedOrderForAction && handlePrint(selectedOrderForAction)}>
                        <ListItemIcon><Print fontSize="small" color="warning" /></ListItemIcon>
                        <ListItemText sx={{ color: 'warning.main' }}>Print</ListItemText>
                    </MenuItem>
                )}
                {hasPermission('DELETE') && (
                    <MenuItem onClick={() => selectedOrderForAction && handleOpenDelete(selectedOrderForAction)}>
                        <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
                        <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
                    </MenuItem>
                )}
            </Menu>

            {/* Orders Table */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: theme.palette.primary.main, '& th': { color: 'white', fontWeight: 'bold', fontSize: '0.95rem' } }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Customer</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                    <TableCell align="right">Balance</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((order, index) => (
                                    <TableRow key={order.id} hover sx={{ '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover } }}>
                                        <TableCell><Chip label={page * rowsPerPage + index + 1} color="primary" size="small" /></TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <CalendarMonth color="action" sx={{ mr: 1, fontSize: 16 }} />
                                                {formatCreatedAtDate(order.created_at)}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'medium' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Person color="action" sx={{ mr: 1, fontSize: 16 }} />
                                                {order.customer_name}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                                            {formatCurrency(order.total_amount)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', color: parseFloat(order.balance) >= 0 ? 'success.main' : 'error.main' }}>
                                            {formatCurrency(order.balance)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={order.order_type?.replace('_', ' ').toUpperCase()} color={getOrderTypeColor(order.order_type)} size="small" />
                                        </TableCell>
                                        <TableCell>
                                            <Chip icon={getStatusIcon(order.order_status)} label={getStatusLabel(order.order_status)} color={getStatusColor(order.order_status)} size="small" />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Actions">
                                                <IconButton 
                                                    sx={{ color: theme.palette.action.main }}
                                                    onClick={(e) => handleActionMenuOpen(e, order)}
                                                    size="small"
                                                >
                                                    <MoreVert />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                            
                            {/* Footer with Totals */}
                            {filteredData.length > 0 && (
                                <TableBody>
                                    <TableRow sx={{
                                        backgroundColor: theme.palette.primary.light + '20',
                                        borderTop: '3px solid',
                                        borderTopColor: theme.palette.primary.main,
                                        '& td': {
                                            fontWeight: 'bold',
                                            py: 2.5
                                        }
                                    }}>
                                        <TableCell colSpan={2} align="right">
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                                Totals ({filteredTotals.totalCount} orders):
                                            </Typography>
                                        </TableCell>
                                        <TableCell></TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                                                    {formatCurrency(filteredTotals.totalAmount)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Total Revenue
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                                    {formatCurrency(filteredTotals.totalBalance)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Outstanding Balance
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell colSpan={3}>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                <Chip
                                                    icon={<Receipt />}
                                                    label={`${filteredTotals.directPayments} Direct`}
                                                    size="small"
                                                    color="success"
                                                />
                                                <Chip
                                                    icon={<AccountBalance />}
                                                    label={`${filteredTotals.loanOrders} Loans`}
                                                    size="small"
                                                    color="warning"
                                                />
                                                <Chip
                                                    icon={<CheckCircle />}
                                                    label={`${filteredTotals.completedOrders} Completed`}
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                />
                                                <Chip
                                                    icon={<Pending />}
                                                    label={`${filteredTotals.pendingOrders} Pending`}
                                                    size="small"
                                                    color="warning"
                                                    variant="outlined"
                                                />
                                                {filteredTotals.quotationOrders > 0 && (
                                                    <Chip
                                                        icon={<Assignment />}
                                                        label={`${filteredTotals.quotationOrders} Quotes`}
                                                        size="small"
                                                        color="info"
                                                        variant="outlined"
                                                    />
                                                )}
                                                {filteredTotals.cancelledOrders > 0 && (
                                                    <Chip
                                                        icon={<Cancel />}
                                                        label={`${filteredTotals.cancelledOrders} Cancelled`}
                                                        size="small"
                                                        color="error"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            )}
                        </Table>
                    </TableContainer>
                    <TablePagination rowsPerPageOptions={[5, 10, 25, 50, 100]} component="div" count={filteredData.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
                </Paper>
            </motion.div>

            {/* Status Update Dialog */}
            <Dialog open={openStatusDialog} onClose={(event, reason) => { if (reason !== 'backdropClick') handleCloseStatusDialog(); }} TransitionComponent={Transition} fullWidth maxWidth="xs">
                <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                    <SwapHoriz sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Update Order Status
                </DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Box sx={{ mt: 2 }}>
                            {/* Current Status Display */}
                            <Paper elevation={0} sx={{ p: 2, mb: 3, backgroundColor: theme.palette.grey[100], borderRadius: 2 }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>Order #{selectedRecord.id}</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box>
                                        <Typography variant="subtitle2">{selectedRecord.customer_name}</Typography>
                                        <Typography variant="body2" color="text.secondary">{formatCurrency(selectedRecord.total_amount)}</Typography>
                                    </Box>
                                    <Chip 
                                        icon={getStatusIcon(selectedRecord.order_status)} 
                                        label={getStatusLabel(selectedRecord.order_status)} 
                                        color={getStatusColor(selectedRecord.order_status)} 
                                    />
                                </Box>
                            </Paper>

                            {/* Status Select */}
                            <FormControl fullWidth error={!!statusError}>
                                <InputLabel>New Status</InputLabel>
                                <Select
                                    value={selectedStatus}
                                    onChange={handleStatusChange}
                                    label="New Status"
                                >
                                    <MenuItem value="pending">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Pending fontSize="small" color="warning" />
                                            Pending
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="completed">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CheckCircle fontSize="small" color="success" />
                                            Completed
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="quotation">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Assignment fontSize="small" color="info" />
                                            Quotation
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="cancelled">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Cancel fontSize="small" color="error" />
                                            Cancelled
                                        </Box>
                                    </MenuItem>
                                </Select>
                                {statusError && <FormHelperText>{statusError}</FormHelperText>}
                            </FormControl>

                            {/* Warning for cancellation */}
                            {selectedStatus === 'cancelled' && selectedRecord?.order_status !== 'cancelled' && (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    <Typography variant="body2">
                                        <strong>Warning:</strong> Cancelling this order will restore the product stock.
                                    </Typography>
                                </Alert>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={handleCloseStatusDialog} color="error" variant="contained" disabled={updatingStatus}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleUpdateStatus} 
                        color="primary" 
                        variant="contained" 
                        disabled={updatingStatus || !selectedStatus || selectedStatus === selectedRecord?.order_status}
                        startIcon={updatingStatus ? <CircularProgress size={16} /> : <CheckCircle />}
                    >
                        {updatingStatus ? 'Updating...' : 'Update Status'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Order Details Dialog */}
            <Dialog open={openView} onClose={(event, reason) => { if (reason !== 'backdropClick') handleCloseView(); }} TransitionComponent={Transition} fullWidth maxWidth="md" scroll="paper">
                <DialogTitle sx={{ textAlign: 'center' }}><b>Order Details - #{selectedRecord?.id}</b></DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <>
                            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                                <Tab label="Details" icon={<Description />} iconPosition="start" />
                                <Tab label="Items" icon={<ShoppingCart />} iconPosition="start" />
                            </Tabs>
                            {activeTab === 0 && (
                                <Box sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, p: 2, backgroundColor: theme.palette.grey[100], borderRadius: 2 }}>
                                        <Avatar sx={{ backgroundColor: theme.palette.primary.light, color: theme.palette.primary.main, mr: 2 }}><Person /></Avatar>
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{selectedRecord.customer_name}</Typography>
                                            <Typography variant="body2"><Phone sx={{ verticalAlign: 'middle', fontSize: 16, mr: 0.5 }} />{selectedRecord.customer_phone}</Typography>
                                        </Box>
                                    </Box>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={4}>
                                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Cash Given</Typography>
                                                <Typography variant="h6">{formatCurrency(selectedRecord.customer_given_cash)}</Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Balance</Typography>
                                                <Typography variant="h6" sx={{ color: parseFloat(selectedRecord.balance) >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                                                    {formatCurrency(selectedRecord.balance)}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Order Total</Typography>
                                                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{formatCurrency(selectedRecord.total_amount)}</Typography>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                    {/* System Information - Card Style */}
                                    <SystemInformation
                                        createdBy={selectedRecord.created_by}
                                        updatedBy={selectedRecord.updated_by}
                                        createdAt={selectedRecord.created_at}
                                        updatedAt={selectedRecord.updated_at}
                                        title="Order System Information"
                                    />
                                </Box>
                            )}
                            {activeTab === 1 && (
                                <Box sx={{ p: 2 }}>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Purchased Items</Typography>
                                    <TableContainer component={Paper} elevation={0}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: theme.palette.primary.main, '& th': { color: 'white', fontWeight: 'bold' } }}>
                                                    <TableCell>Product</TableCell>
                                                    <TableCell>Category</TableCell>
                                                    <TableCell align="center">Unit</TableCell>
                                                    <TableCell align="right">Price</TableCell>
                                                    <TableCell align="right">Qty</TableCell>
                                                    <TableCell align="right">Total</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {selectedRecord.items && (() => {
                                                    try {
                                                        const items = typeof selectedRecord.items === 'string' ? JSON.parse(selectedRecord.items) : selectedRecord.items;
                                                        return items.map((item, index) => {
                                                            // Get unit name from item or from units data
                                                            let unitDisplay = 'N/A';
                                                            if (item.unit_name && item.unit_name !== 'N/A') {
                                                                unitDisplay = item.unit_name;
                                                            } else if (item.unit_id) {
                                                                unitDisplay = getUnitName(item.unit_id);
                                                            } else if (item.unit) {
                                                                unitDisplay = item.unit;
                                                            }
                                                            
                                                            return (
                                                                <TableRow key={index}>
                                                                    <TableCell>{item.name}</TableCell>
                                                                    <TableCell>{item.category || 'N/A'}</TableCell>
                                                                    <TableCell align="center">
                                                                        <Chip
                                                                            icon={<Scale fontSize="small" />}
                                                                            label={unitDisplay}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            color="info"
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell align="right">{formatCurrency(item.price)}</TableCell>
                                                                    <TableCell align="right">{formatNumberWithComma(item.quantity)}</TableCell>
                                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(item.price * item.quantity)}</TableCell>
                                                                </TableRow>
                                                            );
                                                        });
                                                    } catch (e) {
                                                        return <TableRow><TableCell colSpan={6}>Error parsing items</TableCell></TableRow>;
                                                    }
                                                })()}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    {hasPermission('PRINT') && selectedRecord && (
                        <Button onClick={() => handlePrint(selectedRecord)} color="info" variant="contained"><Print sx={{ mr: 1 }} /> Print</Button>
                    )}
                    <Button onClick={handleCloseView} color="error" variant="contained">Close</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDelete} onClose={(event, reason) => { if (reason !== 'backdropClick') handleCloseDelete(); }} TransitionComponent={Transition}>
                <DialogTitle sx={{ textAlign: 'center' }}>Delete Order</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>Are you sure you want to delete this order?</Typography>
                    {selectedRecord && (
                        <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                            Order #{selectedRecord.id}
                            <Box sx={{ mt: 1 }}>
                                <Chip icon={<Person />} label={selectedRecord.customer_name} color="primary" variant="outlined" size="small" sx={{ mr: 1 }} />
                                <Chip label={formatCurrency(selectedRecord.total_amount)} color="error" variant="outlined" size="small" />
                            </Box>
                        </Typography>
                    )}
                    <Typography variant="body2" color="error" sx={{ textAlign: 'center', mt: 2 }}>Warning: This action cannot be undone! Stock will be restored.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseDelete} variant="contained">Cancel</Button>
                    <Button onClick={handleDeleteOrder} color="primary" variant="contained">Confirm Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Filter Dialog with Enhanced Date Range Options */}
            <Dialog open={filterOptions} TransitionComponent={Transition} fullWidth maxWidth="sm" onClose={(event, reason) => { if (reason !== 'backdropClick') handleCloseOptions(); }}>
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <FilterIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Filter Options
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
                        {/* Quick Date Range Filters */}
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                            <DateRange sx={{ verticalAlign: 'middle', mr: 1, fontSize: 18 }} />
                            Quick Date Ranges
                        </Typography>
                        <Grid container spacing={1}>
                            <Grid item xs={6}>
                                <Button 
                                    variant={quickFilter === 'today' ? 'contained' : 'outlined'} 
                                    color="primary" 
                                    size="small" 
                                    fullWidth
                                    startIcon={<TodayIcon />}
                                    onClick={() => applyQuickFilter('today')}
                                    sx={{ textTransform: 'none', borderRadius: '8px', justifyContent: 'flex-start' }}
                                >
                                    Today
                                </Button>
                            </Grid>
                            <Grid item xs={6}>
                                <Button 
                                    variant={quickFilter === 'yesterday' ? 'contained' : 'outlined'} 
                                    color="primary" 
                                    size="small" 
                                    fullWidth
                                    startIcon={<CalendarToday />}
                                    onClick={() => applyQuickFilter('yesterday')}
                                    sx={{ textTransform: 'none', borderRadius: '8px', justifyContent: 'flex-start' }}
                                >
                                    Yesterday
                                </Button>
                            </Grid>
                            <Grid item xs={6}>
                                <Button 
                                    variant={quickFilter === 'this_week' ? 'contained' : 'outlined'} 
                                    color="primary" 
                                    size="small" 
                                    fullWidth
                                    startIcon={<Weekend />}
                                    onClick={() => applyQuickFilter('this_week')}
                                    sx={{ textTransform: 'none', borderRadius: '8px', justifyContent: 'flex-start' }}
                                >
                                    This Week
                                </Button>
                            </Grid>
                            <Grid item xs={6}>
                                <Button 
                                    variant={quickFilter === 'last_7_days' ? 'contained' : 'outlined'} 
                                    color="primary" 
                                    size="small" 
                                    fullWidth
                                    startIcon={<Timeline />}
                                    onClick={() => applyQuickFilter('last_7_days')}
                                    sx={{ textTransform: 'none', borderRadius: '8px', justifyContent: 'flex-start' }}
                                >
                                    Last 7 Days
                                </Button>
                            </Grid>
                            <Grid item xs={6}>
                                <Button 
                                    variant={quickFilter === 'this_month' ? 'contained' : 'outlined'} 
                                    color="primary" 
                                    size="small" 
                                    fullWidth
                                    startIcon={<EventNote />}
                                    onClick={() => applyQuickFilter('this_month')}
                                    sx={{ textTransform: 'none', borderRadius: '8px', justifyContent: 'flex-start' }}
                                >
                                    This Month
                                </Button>
                            </Grid>
                            <Grid item xs={6}>
                                <Button 
                                    variant={quickFilter === 'last_month' ? 'contained' : 'outlined'} 
                                    color="primary" 
                                    size="small" 
                                    fullWidth
                                    startIcon={<EventNote />}
                                    onClick={() => applyQuickFilter('last_month')}
                                    sx={{ textTransform: 'none', borderRadius: '8px', justifyContent: 'flex-start' }}
                                >
                                    Last Month
                                </Button>
                            </Grid>
                            <Grid item xs={6}>
                                <Button 
                                    variant={quickFilter === 'this_quarter' ? 'contained' : 'outlined'} 
                                    color="primary" 
                                    size="small" 
                                    fullWidth
                                    startIcon={<CalendarMonth />}
                                    onClick={() => applyQuickFilter('this_quarter')}
                                    sx={{ textTransform: 'none', borderRadius: '8px', justifyContent: 'flex-start' }}
                                >
                                    This Quarter
                                </Button>
                            </Grid>
                            <Grid item xs={6}>
                                <Button 
                                    variant={quickFilter === 'this_year' ? 'contained' : 'outlined'} 
                                    color="primary" 
                                    size="small" 
                                    fullWidth
                                    startIcon={<CalendarMonth />}
                                    onClick={() => applyQuickFilter('this_year')}
                                    sx={{ textTransform: 'none', borderRadius: '8px', justifyContent: 'flex-start' }}
                                >
                                    This Year
                                </Button>
                            </Grid>
                            <Grid item xs={12}>
                                <Button 
                                    variant={quickFilter === 'all' ? 'contained' : 'outlined'} 
                                    color="secondary" 
                                    size="small" 
                                    fullWidth
                                    onClick={() => applyQuickFilter('all')}
                                    sx={{ textTransform: 'none', borderRadius: '8px', justifyContent: 'flex-start' }}
                                >
                                    All Time
                                </Button>
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 1 }} />

                        {/* Order Type Filter */}
                        <FormControl fullWidth size="small">
                            <InputLabel>Order Type</InputLabel>
                            <Select name="orderType" value={filters.orderType} onChange={handleFilterChange} label="Order Type">
                                <MenuItem value="">All Types</MenuItem>
                                <MenuItem value="direct_payment">Direct Payment</MenuItem>
                                <MenuItem value="loan_order">Loan Order</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Order Status Filter */}
                        <FormControl fullWidth size="small">
                            <InputLabel>Order Status</InputLabel>
                            <Select name="orderStatus" value={filters.orderStatus} onChange={handleFilterChange} label="Order Status">
                                <MenuItem value="">All Status</MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="completed">Completed</MenuItem>
                                <MenuItem value="quotation">Quotation</MenuItem>
                                <MenuItem value="cancelled">Cancelled</MenuItem>
                            </Select>
                        </FormControl>

                        <Divider sx={{ my: 1 }} />

                        {/* Custom Date Range */}
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                            <DateRange sx={{ verticalAlign: 'middle', mr: 1, fontSize: 18 }} />
                            Custom Date Range
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField 
                                    fullWidth 
                                    label="From Date" 
                                    type="date" 
                                    name="dateFrom" 
                                    value={filters.dateFrom} 
                                    onChange={handleFilterChange} 
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField 
                                    fullWidth 
                                    label="To Date" 
                                    type="date" 
                                    name="dateTo" 
                                    value={filters.dateTo} 
                                    onChange={handleFilterChange} 
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button onClick={clearFilters} color="warning" variant="outlined" startIcon={<CloseIcon />}>
                        Clear All
                    </Button>
                    <Button color="error" onClick={handleCloseOptions} variant="contained">
                        Close
                    </Button>
                    <Button onClick={applyFilters} color="primary" variant="contained" startIcon={<FilterIcon />}>
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
            </Snackbar>
        </Container>
    );
};

export default History;